import type { AxiosError, AxiosResponse } from 'axios';
import _ from 'lodash';
import pino from 'pino';

import { userTeamProperties } from '../constants/appConstants';
import type { GetTeamsArgs, GetTeamsResponse, TeamUsers } from '../queries/teamQuery';
import type { User } from '../zustandStore/slices';

import HttpRepository from './httpRepository';
import type { OutOfOffice, TeamProperties } from './requestRepository';
import UserRepository from './userRepository';

const logger = pino();
const TEAMS_BATCH_SIZE = 10;

type Team = {
	teamId: string;
};

export type TeamProperty = {
	propertyKey: string;
	propertyValue: string;
};

type DecoratedUser = User & {
	teamId: string;
	hasOpenRequests: boolean;
	isLastAdmin: boolean;
	requesters: string[];
};

type Currency = {
	code: string;
	name: string;
};

export const TeamRepository = {
	createTeam: async ({ teamName }: { teamName: string }) => {
		const response: AxiosResponse<Team, AxiosError> = await HttpRepository.tradeshiftGo().post('/rest/teams', {
			teamName,
		});
		return response.data;
	},

	getAllTeams: async () => {
		const response: AxiosResponse<GetTeamsResponse[]> = await HttpRepository.tradeshiftGo().get('/rest/teams');
		return response.data;
	},

	getTeamsPage: async (params: GetTeamsArgs) => {
		const response: AxiosResponse<GetTeamsResponse[], AxiosError> = await HttpRepository.tradeshiftGo().get(
			'/rest/teams',
			{
				params,
			},
		);
		return response.data;
	},

	getAllTeamsData: async ({ state }: GetTeamsArgs) => {
		let result = [] as any[];
		let page = 0;
		let totalCount: number | null = null;
		const params: GetTeamsArgs = state ? { state } : {};
		params.limit = TEAMS_BATCH_SIZE;

		while (page === 0 || (totalCount != null && page * params.limit < totalCount)) {
			const teamsPage = await TeamRepository.getTeamsPage({ ...params, offset: page * params.limit });
			result = [...result, ...teamsPage];
			page = page + 1;
			if (totalCount === null) {
				totalCount = (teamsPage && teamsPage[0] && teamsPage[0].teamsCount) || 0;
			}
		}
		return result;
	},

	getTeams: async ({ state, limit, page }: GetTeamsArgs & { page?: number }) => {
		const params: GetTeamsArgs = state ? { state } : {};
		if (Number.isInteger(limit) && Number.isInteger(page) && limit != null && limit > 1 && page != null && page >= 0) {
			params.limit = limit;
			params.offset = limit * page;
			return TeamRepository.getTeamsPage(params);
		} else {
			if (!state) {
				return;
			}
			return TeamRepository.getAllTeamsData({ state });
		}
	},
	getTeam: async ({ teamId }: { teamId: string | string[] | { teamId: string } }) => {
		try {
			const teamID = typeof teamId === 'object' ? (Array.isArray(teamId) ? teamId?.[0] : teamId.teamId) : teamId;
			const response: AxiosResponse<GetTeamsResponse, AxiosError> = await HttpRepository.tradeshiftGo().get(
				`/rest/teams/${teamID}`,
			);
			return response.data;
		} catch (error) {
			logger.error({ message: 'Error getting team', error });
			return {} as GetTeamsResponse;
		}
	},

	getReport: async (path: string) => {
		const response: AxiosResponse = await HttpRepository.tradeshiftGo().get(path);
		return response;
	},

	getTeamProperty: async ({ teamId, propertyKey }: { teamId: string; propertyKey: string }) => {
		const response: AxiosResponse<TeamProperty> = await HttpRepository.tradeshiftGo().get(
			`/rest/teams/${teamId}/properties/${propertyKey}`,
		);
		return response.data;
	},

	saveTeamName: async ({ teamId, teamName }: { teamId: string; teamName: string }) => {
		const response: AxiosResponse<Team> = await HttpRepository.tradeshiftGo().put(`/rest/teams/${teamId}/updateName`, {
			teamName,
		});
		return response.data;
	},

	saveTeamProperty: async ({
		teamId,
		propertyKey,
		propertyValue,
	}: {
		teamId: string;
		propertyKey: string;
		propertyValue: string;
	}) => {
		const response: AxiosResponse<TeamProperty> = await HttpRepository.tradeshiftGo().put(
			`/rest/teams/${teamId}/properties/${propertyKey}`,
			{ propertyValue },
		);
		return response.data;
	},

	activateTeam: async (teamId: string) => {
		await HttpRepository.tradeshiftGo().post(`/rest/teams/${teamId}/activate`);
	},

	deactivateTeam: async (teamId: string) => {
		await HttpRepository.tradeshiftGo().post(`/rest/teams/${teamId}/deactivate`);
	},

	getTeamUsers: async (teamId: string, query: string) => {
		if (!teamId) {
			throw new Error('TeamId is required');
		}
		const response: AxiosResponse<TeamUsers> = await HttpRepository.tradeshiftGo().get(
			`/rest/teams/${teamId}/users${query}`,
		);
		return response.data;
	},

	setUserTeamProperty: async (userId: string, teamId: string, propertyKey: string, propertyValue: string) => {
		const response: AxiosResponse<any> = await HttpRepository.tradeshiftGo().put(
			`/rest/teams/${teamId}/users/${userId}/properties/${propertyKey}`,
			{ propertyValue },
		);
		return response.data;
	},
	getDecoratedUser: async ({ teamId, userId }: { teamId: string; userId: string }) => {
		const response: AxiosResponse<DecoratedUser, AxiosError> = await HttpRepository.tradeshiftGo().get(
			`/rest/teams/${teamId}/users/${userId}`,
		);
		return response.data;
	},

	removeUserFromTeam: async (teamId: string, userId: string): Promise<AxiosResponse> => {
		return HttpRepository.tradeshiftGo().delete(`/rest/teams/${teamId}/users/${userId}`);
	},

	getCurrencies: async (teamId: string) => {
		const response: AxiosResponse<Currency[]> = await HttpRepository.tradeshiftGo().get(
			`/rest/teams/${teamId}/currencies`,
		);
		return response.data;
	},

	getUserTeamProperties: async ({ userId, teamId }: { userId: string; teamId: string }) => {
		if (!userId) {
			const user = await UserRepository.getUser();
			userId = user?.userId;
		}
		const response: AxiosResponse<TeamProperties[]> = await HttpRepository.tradeshiftGo().get(
			`/rest/teams/${teamId}/users/${userId}/properties`,
		);
		return response.data;
	},

	getOooSettings: async ({ userId, teamId }: { userId: string; teamId: string }) => {
		if (!userId) {
			const user = await UserRepository.getUser();
			userId = user?.userId;
		}
		const properties = await TeamRepository.getUserTeamProperties({ userId, teamId });
		const outOfOfficeKeys = Object.values(userTeamProperties.outOfOffice);
		return properties.reduce<OutOfOffice>((output, property) => {
			const propertyKey = Object.keys(property)[0];
			if (propertyKey && outOfOfficeKeys.includes(propertyKey)) {
				output[propertyKey] = property[propertyKey];
			}
			return output;
		}, {} as OutOfOffice);
	},
	setUserTeamProperties: async ({
		userId,
		teamId,
		properties,
	}: {
		userId: string | undefined;
		teamId: string;
		properties: Partial<TeamProperties>[];
	}) => {
		if (!userId) {
			const user = await UserRepository.getUser();
			userId = user?.userId;
		}
		return HttpRepository.tradeshiftGo().put(`/rest/teams/${teamId}/users/${userId}/properties`, { properties });
	},
};
